import { ProgCss } from "./ProgCss.Generated";
import { addTooltipToTarget } from "./tooltips";

export interface PerfData {
    RegisteredPhases: {
        PhaseName: string;
        TimeOffset: number;
    }[];
    ElapsedMS: number;
    CpuMS: number;
    BytesInUse: number;
    QueryCount: number;
    TotalQueryMS: number;
    Queries: {
        Text: string;
        R: number;
        GB: number;
        Milliseconds: number;
    }[];
};

export interface ProgPerformanceTiming {
    navigationStart?: number,
    requestStart: number,
    responseStart: number,
    responseEnd: number,
    domInteractive?: number,
    domContentLoadedEventEnd?: number,
    domComplete: number,
    loadEventEnd?: number,
    firstFreeFrame?: number,
}

export function logNavigationTiming(perfTiming: ProgPerformanceTiming) {
    const perfTip = document.getElementsByClassName(ProgCss.PerfTraceInfo)[0];
    const perfTraceDataEl = document.getElementById("PerfTraceData");
    const perfDataJson = perfTraceDataEl?.getAttribute("data-json");
    const parsedPerf = perfDataJson != null ? JSON.parse(perfDataJson) as PerfData : null;

    if (!(perfTip instanceof HTMLElement) || !perfTraceDataEl || !parsedPerf) return;
    if (perfTiming.loadEventEnd === undefined) {
        perfTiming.loadEventEnd = performance.now();
    }

    requestAnimationFrame(() => {
        document.body.getBoundingClientRect();
        const firstFreeFrame = perfTiming.firstFreeFrame ??= performance.now();

        const perfInfoHtml = renderPerfInfo(parsedPerf, firstFreeFrame, perfTiming, perfTip);
        addTooltipToTarget(perfTip, [perfInfoHtml], true, true);
    });
}

function renderPerfInfo(parsedPerf: PerfData, firstFreeFrame: number, perfTiming: ProgPerformanceTiming, perfTip: HTMLElement): HTMLElement {
    const serverSideTimingReport = parsedPerf.RegisteredPhases === null || parsedPerf.RegisteredPhases === undefined ? '' : parsedPerf.RegisteredPhases.map(phase => "\n" + phase.PhaseName + ": " + phase.TimeOffset.toFixed(0) + "ms").join("");
    const queriesEl = document.createElement("ol");
    for (const query of parsedPerf.Queries) {
        const queryPreEl = document.createElement("pre");
        queryPreEl.classList.add(ProgCss.PerfTraceInfo_query);
        queryPreEl.style.backgroundColor = "rgb(" + query.R + "," + query.GB + "," + query.GB + ")";
        queryPreEl.append(query.Text);
        const perfItem = document.createElement("li");
        perfItem.style.marginTop = "0.5em";
        perfItem.append(queryPreEl, " took " + query.Milliseconds.toFixed(1) + "ms")
        queriesEl.append(perfItem);
    }

    const momentNames: (keyof ProgPerformanceTiming)[] = ["navigationStart", "requestStart", "responseStart", "responseEnd", "domInteractive", "domContentLoadedEventEnd", "domComplete", "loadEventEnd", "firstFreeFrame"];

    const timingReport = !perfTiming
        ? ""
        : "\n" + momentNames
            .map(name => ({ name, timestamp: perfTiming[name] }))
            .filter(o => o.timestamp != null)
            .map(o => ({ name: o.name, timestamp: o.timestamp ?? 0 }))
            .flatMap((_, idx, arr) => idx == 0 ? [] : [{ before: arr[idx - 1], after: arr[idx] }])
            .map(o => `\n${o.after.name} <- ${o.before.name}: ${(o.after.timestamp - o.before.timestamp).toFixed(1)}ms`)
            .join("");

    const clientSideObservedRequestDuration = perfTiming.responseStart - perfTiming.requestStart - parsedPerf.ElapsedMS;

    perfTip.append(`db:${parsedPerf.TotalQueryMS.toFixed(0)}\n`
        + `req:${(perfTiming.responseStart - perfTiming.requestStart).toFixed(0)}\n`
        + `html:${(firstFreeFrame - perfTiming.responseEnd).toFixed(0)}\n`);

    const timingReportEl = document.createElement("pre");
    timingReportEl.classList.add(ProgCss.PerfTraceInfo_timingReport);
    timingReportEl.append((""
        + "Overall client-side: " + (firstFreeFrame - perfTiming.responseEnd).toFixed(1) + "ms\n"
        + "Server-side elapsed: " + parsedPerf.ElapsedMS.toFixed(1) + "ms\n"
        + "     ... + overhead: " + clientSideObservedRequestDuration.toFixed(1) + "ms\n"
        + "Server-side DB-time: " + parsedPerf.TotalQueryMS.toFixed(1) + "ms\n"
        + "Server-side mem-use: " + (parsedPerf.BytesInUse / 1024 / 1024).toFixed(0) + "MB\n"
        + serverSideTimingReport
        + timingReport).trim());

    const queriesSummary = document.createElement("div");
    queriesSummary.append(parsedPerf.QueryCount + " queries took " + parsedPerf.TotalQueryMS.toFixed(1) + "ms.");

    const qd = document.createElement("div");
    qd.style.width = "calc(100vw - 5em)";
    qd.style.fontSize = "13px";
    qd.append(timingReportEl, queriesSummary, queriesEl);
    return qd;
}
